import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { IFilter, IPermiso, IPopulate, IQueryParam } from 'modelos/src';
import { Estados, IAlerta } from 'modelosSeguridad/src';
import {
  IColumnas,
  ICrearBoton,
  IExportarBoton,
} from '../../../../auxiliares/tabla/tabla.component';
import {
  IAcciones,
  TonoEstado,
} from '../../../../auxiliares/tabla/tabla.interfaces';
import { estadoHtml } from '../../../../auxiliares/tabla/celdas';
import {
  IFiltroTabla,
  IRegExpSearch,
} from '../../../../auxiliares/tabla/filtro/filtro.component';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { getUrlFotoVecino } from '../../../../auxiliares/servicios/vecino-foto.helper';
import {
  escaparHtml,
  esColorHex,
} from '../../../../auxiliares/html-seguro.util';
import { DialogService } from '../../../../auxiliares/dialog/dialog.service';
import { IntegracionesService } from '../../../../auxiliares/servicios/http/integraciones.service';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import { LoginService } from '../../../login/login.service';
import { CrearAlertaSeguridadComponent } from '../crear-alerta-seguridad/crear-alerta-seguridad.component';
import { DescargasService } from '../../../navigation/descargas/service';
import { TratarAlertaSeguridadComponent } from '../tratar-alerta-seguridad/tratar-alerta-seguridad.component';
import { PreviewAlertaSeguridadComponent } from '../preview-alerta-seguridad/preview-alerta-seguridad.component';
import { FormularioDialogService } from '../../../../auxiliares/servicios/formulario-dialog.service';

interface SolapaAlertas {
  name: string;
  datos?: IAlerta[];
  totalCount: number;
  pageSize: number;
  columnas?: IColumnas<IAlerta>[];
  search: IRegExpSearch;
  filtros: IFiltroTabla[];
  estados: Estados[];
  query: IQueryParam;
}

const ESTADOS_ACTIVAS: Estados[] = [
  'Sin Tratamiento',
  'Pendiente',
  'En Proceso',
  'Liberada',
  'Móvil Enviado',
  'Móvil En Destino',
  'Derivada Externa',
  'Derivada Compartida',
];

const ESTADOS_FINALIZADAS: Estados[] = [
  'Derivada Finalizada',
  'Derivada Compartida Finalizada',
  'Derivada Externa Finalizada',
  'Finalizada',
];

/**
 * Cada estado tiene su color de siempre (el del mapa y el tratamiento): el
 * badge se pinta de ese color. El ícono acompaña al nombre.
 */
const ESTADO_ALERTA: Record<string, { color: string; icono: string }> = {
  'Sin Tratamiento': { color: '#ef9a9a', icono: 'pending' }, // rojo pastel
  Pendiente: { color: '#ffe082', icono: 'pending' }, // amarillo pastel
  'En Proceso': { color: '#90caf9', icono: 'pending' }, // azul pastel
  'Móvil Enviado': { color: '#b39ddb', icono: 'airport_shuttle' }, // violeta pastel
  'Móvil En Destino': { color: '#ad9dca', icono: 'airport_shuttle' }, // violeta pastel
  Liberada: { color: '#ffcc80', icono: 'lock_open' }, // naranja pastel
  'Derivada Compartida': { color: '#80deea', icono: 'forward' }, // celeste pastel
  'Derivada Externa': { color: '#ffab91', icono: 'forward' }, // coral pastel
  'Derivada Finalizada': { color: '#a5d6a7', icono: 'task_alt' }, // verde pastel
  'Derivada Compartida Finalizada': { color: '#c5e1a5', icono: 'task_alt' }, // verde lima pastel
  'Derivada Externa Finalizada': { color: '#b2dfdb', icono: 'task_alt' }, // verde agua pastel
  Finalizada: { color: '#81c784', icono: 'task_alt' }, // verde pastel más marcado
};

/** "Sin Tratamiento" → "Sin tratamiento": mayúscula sólo al inicio. */
function textoEstado(estado: string): string {
  return estado.charAt(0) + estado.slice(1).toLowerCase();
}

/**
 * Categoría y botón tienen el color que carga un usuario: badge de ese color.
 * Sólo hexadecimal (va dentro de un style; XSS almacenado); sin color válido,
 * badge neutro.
 */
function nombreConColor(nombre: string, color?: string): string {
  return `<span>${estadoHtml({
    texto: nombre,
    tono: 'neutro',
    color: esColorHex(color) ? color : undefined,
  })}</span>`;
}

@Component({
  selector: 'app-alertas-seguridad',
  templateUrl: './alertas-seguridad.component.html',
  styleUrl: './alertas-seguridad.component.scss',
  standalone: false,
})
export class AlertasSeguridadComponent implements OnInit, OnDestroy {
  helper = inject(HelperService);
  private service = inject(IntegracionesService);
  private lista2 = inject(Lista2Service);
  private dialog = inject(MatDialog);
  private formularios = inject(FormularioDialogService);
  private dialogService = inject(DialogService);
  private descargasService = inject(DescargasService);

  public name = 'AlertasSeguridadComponent';
  public permisoActual: IPermiso | undefined = LoginService.getPermisoActual();
  public tieneCredenciales = !!this.permisoActual?.tieneCredencialesSeguridad;

  public selectedTab = 0;

  private activas$?: Subscription;
  private finalizadas$?: Subscription;

  public fotoVecinoActual = '';
  public mostrarVisorFotoVecino = false;

  //POPULATE
  private populate: IPopulate[] = [
    { path: 'boton', select: 'nombre color' },
    { path: 'categoria', select: 'nombre color icono' },
    { path: 'centrosMonitoreo', select: 'nombre' },
    { path: 'centroDerivado', select: 'nombre' },
    { path: 'usuario', select: 'nombre' },
    { path: 'configVecino', select: 'datosPersonales' },
    { path: 'localidad', select: 'nombre' },
  ];

  public solapas: Record<'activas' | 'finalizadas', SolapaAlertas> = {
    activas: this.crearSolapa('AlertasSeguridadActivas', ESTADOS_ACTIVAS, true),
    finalizadas: this.crearSolapa(
      'AlertasSeguridadFinalizadas',
      ESTADOS_FINALIZADAS,
    ),
  };

  public botonCrear: ICrearBoton = {
    mostrar: this.helper.tienePermiso('Alertas Seguridad - Crear'),
    tooltip: 'Crear alerta',
    color: 'primary',
    tipo: 'material',
    icon: 'add',
    accion: () => this.abrirCrearAlerta(),
  };

  public botonExportarActivas: IExportarBoton = {
    mostrar: this.helper.tienePermiso('Alertas Seguridad - Exportar'),
    tooltip: 'Exportar a planilla',
    color: 'primary',
    tipo: 'material',
    icon: 'table_view',
    accion: () =>
      this.exportarSolapa(this.solapas.activas, 'alertas_seguridad_activas'),
  };
  public botonExportarFinalizadas: IExportarBoton = {
    mostrar: this.helper.tienePermiso('Alertas Seguridad - Exportar'),
    tooltip: 'Exportar a planilla',
    color: 'primary',
    tipo: 'material',
    icon: 'table_view',
    accion: () =>
      this.exportarSolapa(
        this.solapas.finalizadas,
        'alertas_seguridad_finalizadas',
      ),
  };

  //CREAR
  //1-SOLAPAS
  private crearSolapa(
    name: string, //Nombre de la solapa
    estados: Estados[], //Estados para los filtros de la solapa
    sinPaginacion = false, //Activas no tiene paginación, finalizada sí tiene paginación
  ): SolapaAlertas {
    return {
      name,
      datos: undefined,
      totalCount: 0,
      pageSize: sinPaginacion ? 0 : +this.helper.getPageSize(name),
      columnas: undefined,
      search: {
        // Sólo campos stored: los virtuals (categoria, configVecino, usuario)
        // no matchean en .find() del backend. Para buscar por vecino/operador
        // en finalizadas se usan los filtros explícitos.
        fields: [
          'direccion',
          'detallesDireccion',
          'estadoActual',
          'entidadExterna',
          'tipo',
        ],
      },
      filtros: this.crearFiltros(estados),
      estados,
      query: {
        page: 0,
        limit: sinPaginacion ? 0 : +this.helper.getPageSize(name),
        ...(sinPaginacion ? { unlimited: true } : {}),
        sort: 'prioridad -fechaCreacion',
        populate: JSON.stringify(this.populate),
      },
    };
  }

  //2- FILTROS
  private crearFiltros(estados: Estados[]): IFiltroTabla[] {
    const estadoFiltro: IFiltroTabla = {
      elementos: estados.map((e) => ({ nombre: e, _id: e })),
      filter: { field: 'estadoActual' },
      label: 'Estado actual',
      rapido: estados.length <= 4,
      rapidoLabel: 'Estado',
      selectLabel: 'nombre',
      selectValue: '_id',
      tipo: 'select',
    };

    const tipoFiltro: IFiltroTabla = {
      elementos: [
        { label: 'Emergencia', value: 'Emergencia' },
        { label: 'Evento', value: 'Evento' },
      ],
      filter: { field: 'tipo' },
      label: 'Tipo',
      rapido: true,
      selectLabel: 'label',
      selectValue: 'value',
      tipo: 'select',
    };

    const counterFiltro: IFiltroTabla = {
      filter: { field: 'counter' },
      label: 'ID #',
      tipo: 'input',
    };

    const fechaFiltro: IFiltroTabla = {
      label: 'Rango de fechas',
      filter: { field: 'fechaCreacion' },
      tipo: 'dateRange',
    };

    const vecinoFiltro: IFiltroTabla = {
      filter: { field: 'idConfigVecino' },
      label: 'Vecino',
      tipo: 'vecino-selector',
    };

    const localidadFiltro: IFiltroTabla = {
      filter: { field: 'idLocalidad' },
      label: 'Localidad',
      tipo: 'localidad-selector',
    };

    return [
      counterFiltro,
      estadoFiltro,
      vecinoFiltro,
      localidadFiltro,
      fechaFiltro,
      tipoFiltro,
    ];
  }

  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  //LISTAR
  //Para listar alertas de seguridad, se deben tener las credenciales
  //LISTAR ALERTAS (UNA SOLAPA DE ALERTAS ACTIVAS (NEUVAS Y EN CURSO) Y OTRA DE ALERTAS FINALIZADAS)
  private normalizarFiltroAlertas(
    filterFromQuery: IFilter<IAlerta>,
    esActivas: boolean,
  ): IFilter<IAlerta> {
    const filter = {
      ...filterFromQuery,
    } as Record<string, any>;

    let $and: any[] = Array.isArray(filter['$and']) ? [...filter['$and']] : [];

    // Convierte el filtro por ID (counter) desde input string a número.
    $and = $and.map((condition) => {
      const c = condition as Record<string, any>;
      if (
        'counter' in c &&
        typeof c['counter'] === 'string' &&
        c['counter'] !== ''
      ) {
        const num = Number(c['counter']);
        if (!isNaN(num)) {
          return { counter: num };
        }
      }
      return condition;
    });

    // Compatibilidad legacy: algunos registros históricos usan idVecino.
    $and = $and.map((condition) => {
      const c = condition as Record<string, any>;
      const idConfigVecino = c['idConfigVecino'];
      if (typeof idConfigVecino === 'string' && idConfigVecino.trim() !== '') {
        return {
          $or: [{ idConfigVecino }, { idVecino: idConfigVecino }],
        };
      }
      return condition;
    });

    // Si el usuario busca por texto numérico, agregar búsqueda exacta por counter.
    const rawSearch =
      $and
        .flatMap((condition) => {
          const c = condition as Record<string, any>;
          return Array.isArray(c['$or']) ? c['$or'] : [];
        })
        .find((clause: Record<string, any>) =>
          Object.prototype.hasOwnProperty.call(clause, 'direccion'),
        )?.direccion?.$regex ?? '';

    if (
      typeof rawSearch === 'string' &&
      rawSearch.trim() !== '' &&
      /^\d+$/.test(rawSearch.trim())
    ) {
      const counterNum = Number(rawSearch.trim());
      // Agregar counter al $or existente para que sea OR con el texto, no AND
      const orIdx = $and.findIndex((c: any) => Array.isArray(c['$or']));
      if (orIdx !== -1) {
        $and[orIdx] = {
          $or: [...($and[orIdx] as any)['$or'], { counter: counterNum }],
        };
      } else {
        $and.push({ counter: counterNum });
      }
    }

    const tieneActiva =
      Object.prototype.hasOwnProperty.call(filter, 'activa') ||
      $and.some((c) => Object.prototype.hasOwnProperty.call(c, 'activa'));

    if (!tieneActiva) {
      $and.push({ activa: esActivas });
    }

    return {
      ...filter,
      ...($and.length ? { $and } : {}),
    } as IFilter<IAlerta>;
  }

  public listarSolapa(solapa: SolapaAlertas): void {
    if (!this.tieneCredenciales) {
      solapa.datos = [];
      solapa.totalCount = 0;
      return;
    }

    const esActivas = solapa.name === 'AlertasSeguridadActivas';
    const filterFromQuery: IFilter<IAlerta> = solapa.query.filter
      ? JSON.parse(solapa.query.filter)
      : {};
    const filtroFinal = this.normalizarFiltroAlertas(
      filterFromQuery,
      esActivas,
    );

    const query: IQueryParam = {
      ...solapa.query,
      filter: JSON.stringify(filtroFinal),
    };

    if (esActivas) {
      this.activas$?.unsubscribe();
      this.activas$ = this.lista2
        .alertasSeguridad(query, 30)
        .subscribe((res) => {
          solapa.totalCount = res?.totalCount || 0;
          const prioAlta = new Set<string>(['Sin Tratamiento', 'Liberada']);
          solapa.datos = (res?.datos || []).sort((a, b) => {
            const pA = prioAlta.has(a.estadoActual ?? '') ? 0 : 1;
            const pB = prioAlta.has(b.estadoActual ?? '') ? 0 : 1;
            if (pA !== pB) return pA - pB;
            return (
              new Date(b.fechaCreacion ?? 0).getTime() -
              new Date(a.fechaCreacion ?? 0).getTime()
            );
          });
        });
    } else {
      this.finalizadas$?.unsubscribe();
      this.finalizadas$ = this.lista2
        .alertasSeguridad(query, 30)
        .subscribe((res) => {
          solapa.totalCount = res?.totalCount || 0;
          solapa.datos = res?.datos || [];
        });
    }
  }

  //AUXILIARES
  private getTiempo(ms: number | undefined | null): string {
    if (!ms) return '--:--:--';
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }

  private setColumnas(solapa: SolapaAlertas): void {
    const esFinalizadas = solapa.name === 'AlertasSeguridadFinalizadas';
    // Sin detalle propio: Atender (activas) o Ver detalle (finalizadas) abren
    // el tratamiento; el PDF va suelto al lado.
    const acciones: IAcciones<IAlerta>[] = [
      {
        tipo: 'material',
        icon: esFinalizadas ? 'search' : 'troubleshoot',
        tooltip: esFinalizadas ? 'Ver detalle' : 'Atender alerta',
        click: (row: IAlerta) => this.abrirTratar(row),
        disabled: () =>
          !this.helper.tienePermiso('Alertas Seguridad - Atender'),
      },
    ];

    if (esFinalizadas) {
      acciones.push({
        tipo: 'material',
        icon: 'picture_as_pdf',
        tooltip: 'Exportar PDF y audio',
        click: (row: IAlerta) => this.abrirPreview(row),
        disabled: () =>
          !this.helper.tienePermiso('Alertas Seguridad - Exportar'),
      });
    }

    solapa.columnas = [
      {
        header: { label: 'ID', sortable: true, sort: 'counter' },
        row: {
          html: (row) => {
            if (row.counter === undefined || row.counter === null) return '';
            return `<span class="tabla-principal">#${escaparHtml(row.counter)}</span>`;
          },
          noData: 'Sin ID',
        },
      },
      {
        header: { label: 'Fecha', sortable: true, sort: 'fechaCreacion' },
        row: {
          fecha: (row) => row.fechaCreacion,
          hace: true,
          noData: 'Sin fecha',
        },
      },
      {
        header: { label: 'Estado', sortable: true, sort: 'estadoActual' },
        row: {
          estado: (row) => {
            if (!row.estadoActual) return undefined;
            const e = ESTADO_ALERTA[row.estadoActual];
            return e
              ? {
                  texto: textoEstado(row.estadoActual),
                  tono: 'neutro' as TonoEstado,
                  color: e.color,
                  icono: e.icono,
                }
              : {
                  texto: textoEstado(row.estadoActual),
                  tono: 'neutro' as TonoEstado,
                  icono: 'question_mark',
                };
          },
          noData: 'Sin estado',
        },
      },
      {
        header: { label: 'Centro de monitoreo' },
        row: {
          html: (row) => {
            const centros = (row.centrosMonitoreo || []).filter(
              (c) => c?.nombre,
            );
            if (!centros.length) return '';
            return `<div class="tabla-celda-col">${centros
              .map((c) => `<span>${escaparHtml(c.nombre)}</span>`)
              .join('')}</div>`;
          },
          noData: 'Sin centro',
        },
      },
      {
        header: { label: 'Categoría' },
        row: {
          html: (row) => {
            if (!row.categoria?.nombre) return '';
            return nombreConColor(row.categoria.nombre, row.categoria.color);
          },
          noData: 'Sin categoría',
        },
      },
      {
        header: { label: 'Usuario' },
        row: {
          html: (row) => {
            const nombre = row.usuario?.nombre;
            if (!nombre) return '';
            return `<div class="tabla-linea">
              <span class="material-symbols-outlined">person</span>
              <span>${escaparHtml(nombre)}</span>
            </div>`;
          },
          noData: 'Sin usuario',
        },
      },
      {
        header: { label: 'Tipo' },
        row: {
          html: (row) => {
            const partes: string[] = [];
            if (row.idEventoExterno) {
              partes.push(
                `<span>${estadoHtml({ texto: 'Evento externo', tono: 'info' })}</span>`,
              );
            }
            if (row.boton?.nombre) {
              partes.push(nombreConColor(row.boton.nombre, row.boton.color));
            } else if (row.tipo) {
              partes.push(`<span>${escaparHtml(row.tipo)}</span>`);
            }
            if (
              row.idCentroDerivado &&
              (!row.tipoDelegacion || row.tipoDelegacion === 'normal')
            ) {
              partes.push(
                `<span>${estadoHtml({ texto: 'Derivada', tono: 'info', icono: 'forward' })}</span>`,
              );
            }
            if (
              row.idCentroDerivado &&
              row.tipoDelegacion === 'visibilidad-compartida'
            ) {
              partes.push(
                `<span>${estadoHtml({ texto: 'Compartida', tono: 'info', icono: 'forward' })}</span>`,
              );
            }
            if (row.tipoDelegacion === 'externa' && row.entidadExterna) {
              partes.push(
                `<span>${estadoHtml({ texto: row.entidadExterna, tono: 'info', icono: 'forward' })}</span>`,
              );
            }
            if (!partes.length) return '';
            return `<div class="tabla-celda-col">${partes.join('')}</div>`;
          },
          // A quién se derivó (antes en el title de cada etiqueta)
          tooltip: (row) => {
            const items: string[] = [];
            if (
              row.idCentroDerivado &&
              (!row.tipoDelegacion || row.tipoDelegacion === 'normal')
            ) {
              items.push(
                `<div>Derivada a: <strong>${escaparHtml(row.centroDerivado?.nombre)}</strong></div>`,
              );
            }
            if (
              row.idCentroDerivado &&
              row.tipoDelegacion === 'visibilidad-compartida'
            ) {
              items.push(
                `<div>Compartida con: <strong>${escaparHtml(row.centroDerivado?.nombre)}</strong></div>`,
              );
            }
            if (row.tipoDelegacion === 'externa' && row.entidadExterna) {
              items.push(
                `<div>Externa: <strong>${escaparHtml(row.entidadExterna)}</strong></div>`,
              );
            }
            return items.length ? items.join('') : undefined;
          },
          noData: 'Sin tipo',
        },
      },
      {
        header: { label: 'Vecino' },
        row: {
          html: (row) => {
            const nombre =
              row.configVecino?.datosPersonales?.nombre || row.vecino?.nombre;
            const dni = row.configVecino?.datosPersonales?.dni;
            const tel = row.configVecino?.datosPersonales?.telefono;
            // Sin datos del vecino la alerta es anónima: es un valor, no una
            // falta de dato
            if (!nombre && !dni && !tel) return '<span>Anónimo</span>';
            const url = getUrlFotoVecino(row.configVecino);
            const urlFoto = url ? escaparHtml(url) : null;
            const foto = urlFoto
              ? `<img src="${urlFoto}" alt="Foto del vecino" class="foto-vecino-avatar" data-foto-vecino="${urlFoto}" title="Ver foto completa" draggable="false" />`
              : `<span class="material-symbols-outlined">person</span>`;
            let html = `<div class="tabla-celda-col">`;
            if (nombre) {
              html += `<div class="tabla-linea">
                ${foto}
                <span class="tabla-principal">${escaparHtml(nombre)}</span>
              </div>`;
            }
            if (dni) {
              html += `<span class="tabla-secundario">DNI ${escaparHtml(dni)}</span>`;
            }
            if (tel) {
              html += `<span class="tabla-secundario">Tel. ${escaparHtml(tel)}</span>`;
            }
            html += `</div>`;
            return html;
          },
        },
      },
      {
        header: { label: 'Localidad' },
        row: {
          parse: (row) => row.localidad?.nombre,
          noData: 'Sin localidad',
        },
      },
      {
        header: { label: 'Dirección' },
        row: {
          html: (row) => {
            if (!row.direccion) return '';
            const dir = HelperService.extraerDireccion(row.direccion);
            return `<div class="tabla-linea">
              <span class="material-symbols-outlined">place</span>
              <span>${escaparHtml(dir)}</span>
            </div>`;
          },
          tooltip: (row) => row.direccion,
          noData: 'Sin dirección',
        },
      },
      {
        header: {
          label: 'Tiempo de respuesta',
          sortable: true,
          sort: 'tiempoRespuesta',
        },
        row: {
          html: (row) => {
            if (!row.tiempoRespuesta) return '';
            return `<div class="tabla-linea">
              <span class="material-symbols-outlined">timer</span>
              <span class="tabla-codigo">${this.getTiempo(row.tiempoRespuesta)}</span>
            </div>`;
          },
          noData: 'Sin tiempo',
        },
      },
      {
        header: { label: 'Acciones' },
        row: {
          field: 'acciones',
          acciones,
        },
      },
    ];
  }

  private abrirCrearAlerta(): void {
    const ref = this.formularios.abrir(CrearAlertaSeguridadComponent, {
      tamano: 'grande',
    });
    ref.afterClosed().subscribe((res) => {
      if (res) this.refrescarTodo();
    });
  }

  private async exportarSolapa(
    solapa: SolapaAlertas,
    nombreBase: string,
  ): Promise<void> {
    if (!this.tieneCredenciales) return;
    const ok = await this.dialogService.confirm(
      'Exportar a planilla',
      '¿Deseás exportar el listado actual? La descarga se preparará en segundo plano.',
    );
    if (!ok) return;

    const esActivas = solapa.name === 'AlertasSeguridadActivas';
    const filterFromQuery: IFilter<IAlerta> = solapa.query.filter
      ? JSON.parse(solapa.query.filter as any)
      : {};
    const filtroFinal = this.normalizarFiltroAlertas(
      filterFromQuery,
      esActivas,
    );

    const query: IQueryParam = {
      ...solapa.query,
      page: 0,
      limit: 0,
      unlimited: true,
      filter: JSON.stringify(filtroFinal),
    };

    // El XLSX se genera server-side (monitoreo) y el gestor de descargas lo
    // prepara en background: no bloquea la pantalla.
    const fecha = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    this.descargasService.agregarDescargaExcel(`${nombreBase}_${fecha}`, () =>
      this.service.exportarAlertasComoBlob(query),
    );
    this.helper.notifSuccess('Preparando la descarga en segundo plano...');
  }

  public verFotoVecino(url: string): void {
    this.fotoVecinoActual = url;
    this.mostrarVisorFotoVecino = true;
  }

  /**
   * La foto del vecino se embebe como HTML crudo dentro de la columna
   * "Vecino" (no es un componente Angular), así que el click se detecta acá
   * por delegación en vez de un binding directo.
   */
  public onVecinoFotoClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    const img = target?.closest?.('[data-foto-vecino]') as HTMLElement | null;
    const url = img?.getAttribute('data-foto-vecino');
    if (url) this.verFotoVecino(url);
  }

  private abrirTratar(alerta: IAlerta): void {
    const ref = this.dialog.open(TratarAlertaSeguridadComponent, {
      data: { idAlerta: alerta._id },
      width: '1080px',
      maxWidth: '96vw',
      maxHeight: '95vh',
      disableClose: true,
      panelClass: 'alerta-tratamiento-dialog',
    });
    ref.afterClosed().subscribe((actualizo) => {
      if (actualizo) this.refrescarTodo();
    });
  }

  private abrirPreview(alerta: IAlerta): void {
    if (!alerta?._id) return;
    this.formularios.abrir(PreviewAlertaSeguridadComponent, {
      tamano: 'grande',
      data: { idAlerta: alerta._id },
      cerrarAfuera: true,
    });
  }
  private refrescarTodo(): void {
    this.listarSolapa(this.solapas.activas);
    this.listarSolapa(this.solapas.finalizadas);
  }

  async ngOnInit(): Promise<void> {
    this.setColumnas(this.solapas.activas);
    this.setColumnas(this.solapas.finalizadas);
    this.refrescarTodo();
  }

  ngOnDestroy(): void {
    this.activas$?.unsubscribe();
    this.finalizadas$?.unsubscribe();
  }
}
